import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Maximize2, X } from "lucide-react";

/**
 * Shared preview frame: a 3:2 image container with fade transitions between
 * srcs, an optional loading veil, an optional empty-state overlay, and a
 * caption placed *below* the frame so text never covers the floor.
 *
 * A "View full-screen" affordance sits top-right of the frame. Tapping it
 * opens a fixed fullscreen overlay that fills the viewport with the current
 * preview at `object-contain` fit (no cropping). Native pinch-zoom is
 * enabled so mobile users can inspect detail. Esc key or tapping the
 * backdrop / close button dismisses.
 */
export default function PreviewFrame({
  src,
  alt,
  imgKey,
  onError,
  loadingLabel,     // when truthy → shows spinner veil
  loadingSwatch,    // optional { image, hex, name } → shown as target preview
  emptyOverlay,     // node rendered when src is empty
  caption,          // { eyebrow, title, sub, error? }
  testid = "viz-preview",
}) {
  const [full, setFull] = useState(false);

  useEffect(() => {
    if (!full) return;
    const onKey = (e) => { if (e.key === "Escape") setFull(false); };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [full]);

  return (
    <>
      <div
        className="relative aspect-[3/2] bg-[#0f1012] overflow-hidden border border-[#8C7355]/15"
        data-testid={testid}
      >
        <AnimatePresence mode="wait" initial={false}>
          {src ? (
            <motion.img
              key={imgKey || src}
              src={src}
              alt={alt}
              loading="eager"
              decoding="async"
              fetchPriority="high"
              width="1600"
              height="1067"
              onError={onError}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35 }}
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : null}
        </AnimatePresence>

        {emptyOverlay && !src && emptyOverlay}

        {/* Full-screen toggle — visible whenever there's an image to expand */}
        {src && !loadingLabel && (
          <button
            type="button"
            onClick={() => setFull(true)}
            aria-label="View full screen"
            data-testid="viz-fullscreen-open"
            className="absolute top-3 right-3 z-10 flex items-center gap-2 bg-[#121315]/70 hover:bg-[#121315]/90 border border-[#8C7355]/30 hover:border-[#C9A876]/60 backdrop-blur-sm px-3 py-2 transition-colors duration-300"
          >
            <Maximize2 className="text-[#C9A876]" size={14} strokeWidth={1.5} />
            <span className="hidden sm:inline text-[#EDE9E2] text-[10px] tracking-[0.28em] uppercase font-body">
              Full screen
            </span>
          </button>
        )}

        {loadingLabel && (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center bg-[#121315]/70"
            data-testid="viz-loading"
          >
            {loadingSwatch?.image ? (
              <div className="flex items-center gap-4 bg-[#1a1b1e]/90 border border-[#8C7355]/25 px-4 py-3">
                <img
                  src={loadingSwatch.image}
                  alt=""
                  width="64"
                  height="64"
                  loading="eager"
                  decoding="async"
                  className="w-16 h-16 object-cover"
                  data-testid="viz-loading-swatch"
                />
                <div>
                  <p className="text-[#C9A876] text-[9px] tracking-[0.32em] uppercase mb-1 font-body">Applying</p>
                  <p className="font-serif-display italic text-[#F5F4F0] text-lg leading-tight">
                    {loadingSwatch.name}
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    <Loader2 className="animate-spin text-[#C9A876]" size={14} />
                    <span className="text-[#D6D1CA]/70 text-[10px] tracking-[0.28em] uppercase font-body">
                      Rendering
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <Loader2 className="animate-spin text-[#C9A876]" size={40} />
                {typeof loadingLabel === "string" && (
                  <p className="mt-3 text-[#D6D1CA]/80 text-[10px] tracking-[0.3em] uppercase font-body">
                    {loadingLabel}
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {caption && (
        <div className="mt-3 md:mt-4" data-testid="viz-caption">
          {caption.eyebrow && (
            <p className="text-[#C9A876] text-[10px] tracking-[0.4em] uppercase mb-1">
              {caption.eyebrow}
            </p>
          )}
          {caption.title && (
            <h2 className="font-serif-display italic text-[#F5F4F0] font-light tracking-tight text-xl md:text-2xl leading-tight">
              {caption.title}
            </h2>
          )}
          {caption.sub && (
            <p className="text-[#D6D1CA]/70 text-xs md:text-sm mt-1 font-body">
              {caption.sub}
            </p>
          )}
          {caption.error && (
            <p className="text-[#C9A876]/80 text-xs mt-2 font-body" data-testid="viz-colour-error">
              {caption.error}
            </p>
          )}
        </div>
      )}

      {/* Full-screen overlay — pinch-zoomable on touch, dismiss on Esc / backdrop / X */}
      <AnimatePresence>
        {full && src && (
          <motion.div
            key="viz-full"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-[100] bg-[#0a0b0c]/95 backdrop-blur-md flex items-center justify-center p-3 sm:p-6"
            onClick={() => setFull(false)}
            data-testid="viz-fullscreen-overlay"
          >
            <motion.img
              key={imgKey || src}
              src={src}
              alt={alt}
              initial={{ scale: 0.985, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.99, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              onError={onError}
              style={{ touchAction: "pinch-zoom" }}
              className="max-w-full max-h-full object-contain select-none"
              draggable={false}
              data-testid="viz-fullscreen-image"
            />

            {/* Caption row — top-left */}
            {caption?.title && (
              <div className="absolute top-4 left-4 sm:top-6 sm:left-6 max-w-[70vw] pointer-events-none">
                {caption.eyebrow && (
                  <p className="text-[#C9A876] text-[10px] tracking-[0.4em] uppercase mb-1">
                    {caption.eyebrow}
                  </p>
                )}
                <h2 className="font-serif-display italic text-[#F5F4F0] font-light tracking-tight text-lg sm:text-2xl leading-tight">
                  {caption.title}
                </h2>
                {caption.sub && (
                  <p className="text-[#D6D1CA]/70 text-xs sm:text-sm mt-1 font-body">
                    {caption.sub}
                  </p>
                )}
              </div>
            )}

            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setFull(false); }}
              aria-label="Close full screen"
              data-testid="viz-fullscreen-close"
              className="absolute top-4 right-4 sm:top-6 sm:right-6 flex items-center gap-2 bg-[#121315]/80 hover:bg-[#121315] border border-[#8C7355]/40 hover:border-[#C9A876]/70 px-3 py-2 transition-colors duration-300"
            >
              <X className="text-[#C9A876]" size={16} strokeWidth={1.5} />
              <span className="hidden sm:inline text-[#EDE9E2] text-[10px] tracking-[0.28em] uppercase font-body">
                Close
              </span>
            </button>

            {/* Hint for mobile users */}
            <p className="hidden sm:block absolute bottom-6 left-1/2 -translate-x-1/2 text-[#D6D1CA]/50 text-[10px] tracking-[0.3em] uppercase font-body pointer-events-none">
              Pinch to zoom · Esc to close
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
